import { istWerktag, tagKurz, werktageSpaeter } from './zeit'

/**
 * **Für Mein Tag markieren** ([Z-20](../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
 *
 * Nils, 05.10.2026: *„Hierbei handelt es sich nicht um eine Änderung der Fälligkeit, sondern um
 * den gewünschten Bearbeitungszeitpunkt."* Eine Markierung ist ein **Tag** (`YYYY-MM-DD`), an
 * dem *ich* daran arbeiten will – privat, wie die Pin-Wiedervorlage
 * ([E-216](../../../docs/ENTSCHEIDUNGEN.md#e-216)), und ohne Uhrzeit: der Tagesplan legt bei jeder
 * Änderung neu (Z-19), eine gespeicherte Uhrzeit wäre nach dem ersten Termin falsch.
 *
 * ## Warum das hier steht
 *
 * Dieselbe Frage – „für welchen Tag gilt diese Markierung heute?" – stellen fünf Ansichten
 * (Detail, Vorlauf, Liste, Telefon, Kalender). Stünde die Antwort fünfmal im JSX, liefe sie beim
 * ersten Freitag auseinander. Alles hier ist rein: **„heute" wird übergeben**, nie gelesen.
 *
 * ## Liegengeblieben wandert mit ([F-95](../../../docs/OFFENE-FRAGEN.md#f-95))
 *
 * Gespeichert bleibt `am`, wie es gesetzt wurde. Gerechnet wird `max(am, heute)`: eine für Freitag
 * markierte, nicht erledigte Aufgabe steht am Montag unter „Heute · seit Fr". Kein Hintergrundlauf
 * schreibt etwas um – ein Wecker, der einmal nicht läuft, verschluckt sonst eine Absicht lautlos.
 */

/** Die drei Knöpfe, die überall angeboten werden. */
export const MARKIER_STUFEN = ['heute', 'morgen', 'uebermorgen'] as const
export type MarkierStufe = (typeof MARKIER_STUFEN)[number]

export const MARKIER_STUFE_LABELS: Record<MarkierStufe, string> = {
  heute: 'Heute',
  morgen: 'Morgen',
  uebermorgen: 'Übermorgen',
}

/** Filterwerte der Listen – die drei Stufen plus Sammelbegriffe. */
export const MARKIER_FILTER = [
  'markiert',
  'heute',
  'morgen',
  'uebermorgen',
  'spaeter',
  'ohne',
] as const
export type MarkierFilter = (typeof MARKIER_FILTER)[number]

export const MARKIER_FILTER_LABELS: Record<MarkierFilter, string> = {
  markiert: 'Markiert',
  heute: 'Heute',
  morgen: 'Morgen',
  uebermorgen: 'Übermorgen',
  spaeter: 'Später',
  ohne: 'Ohne',
}

const TAG_MUSTER = /^\d{4}-\d{2}-\d{2}$/

/** Ein Tagesschlüssel als Kalendertag in Ortszeit – so rechnen die Werktagsfunktionen. */
function alsKalendertag(tag: string): Date {
  if (!TAG_MUSTER.test(tag)) throw new Error(`Unbrauchbarer Tag: ${tag}`)
  const [j, m, t] = tag.split('-').map(Number) as [number, number, number]
  return new Date(j, m - 1, t)
}

/**
 * Zurück zum Schlüssel – **aus den Feldern**, nicht über `tagesschluessel()`: das formatiert einen
 * Zeitpunkt in Berliner Zeit, und Mitternacht Ortszeit auf einem Rechner westlich von UTC ist in
 * Berlin noch der Vortag.
 */
function alsSchluessel(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const t = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${t}`
}

/**
 * **Der Tag hinter einem Knopf.** „Heute" ist heute – auch an einem Samstag, wer dann etwas
 * markiert, meint diesen Tag. „Morgen" und „Übermorgen" zählen **Werktage** (Nils: *„Werktage
 * werden berücksichtigt"*), Feiertage in NRW inklusive: freitags ist „Morgen" der Montag.
 */
export function markierTag(stufe: MarkierStufe, heute: string): string {
  const schritte = MARKIER_STUFEN.indexOf(stufe)
  return schritte === 0 ? heute : alsSchluessel(werktageSpaeter(alsKalendertag(heute), schritte))
}

/** Alle drei Knöpfe mit ihrem Tag – in dieser Reihenfolge angezeigt. */
export function markierTage(heute: string): { stufe: MarkierStufe; tag: string }[] {
  return MARKIER_STUFEN.map((stufe) => ({ stufe, tag: markierTag(stufe, heute) }))
}

/**
 * **Die Spalten der Wochenleiste im Kalender**: heute und die folgenden Werktage. Heute steht
 * immer vorn, auch am Wochenende – sonst hätte, was am Samstag für heute markiert ist, keinen Ort.
 */
export function markierLeiste(heute: string, anzahl = 5): string[] {
  const start = alsKalendertag(heute)
  return Array.from({ length: anzahl }, (_, i) =>
    i === 0 ? heute : alsSchluessel(werktageSpaeter(start, i)),
  )
}

/** Einen Werktag vor oder zurück – für die Pfeile am Tagesplan, die Wochenende und Feiertag überspringen. */
export function werktagSchritt(tag: string, schritt: -1 | 1): string {
  const d = alsKalendertag(tag)
  do d.setDate(d.getDate() + schritt)
  while (!istWerktag(d))
  return alsSchluessel(d)
}

/** Für welchen Tag eine Markierung **heute** gilt: liegengeblieben wandert auf heute. */
export function wirksamerMarkierTag(am: string, heute: string): string {
  return am < heute ? heute : am
}

/** Liegt die Markierung vor heute? Dann trägt die Anzeige „seit …". */
export function istLiegengeblieben(am: string, heute: string): boolean {
  return am < heute
}

/** In welche der Stufen eine Markierung heute fällt – `spaeter` für alles hinter Übermorgen. */
export function markierKlasse(am: string, heute: string): MarkierStufe | 'spaeter' {
  const tag = wirksamerMarkierTag(am, heute)
  for (const { stufe, tag: t } of markierTage(heute)) if (t === tag) return stufe
  /**
   * Zwischen heute und „Morgen" kann ein Wochenende liegen: wer am Freitag den Samstag markiert,
   * meint weder heute noch Montag. Er landet in `spaeter` – ehrlich, statt in eine Stufe gerundet.
   */
  return 'spaeter'
}

/** Passt eine Aufgabe (mit oder ohne Markierung) zu einem Listenfilter? */
export function passtZuMarkierFilter(
  am: string | null,
  heute: string,
  filter: MarkierFilter,
): boolean {
  if (filter === 'ohne') return am === null
  if (am === null) return false
  if (filter === 'markiert') return true
  return markierKlasse(am, heute) === filter
}

/**
 * **Das Wort an der Sonne**: „Heute", „Morgen", „Übermorgen", sonst „Do., 08.10.". Bei einer
 * liegengebliebenen Markierung hängt „seit Fr." an – damit niemand glaubt, er habe sie heute
 * gesetzt.
 */
export function markierWort(am: string, heute: string): string {
  const klasse = markierKlasse(am, heute)
  const wort = klasse === 'spaeter' ? tagKurz(am) : MARKIER_STUFE_LABELS[klasse]
  if (!istLiegengeblieben(am, heute)) return wort
  const wochentag = tagKurz(am).split(',')[0]
  return `${wort} · seit ${wochentag}`
}
